import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tooltip,
  CircularProgress,
  IconButton,
  Paper,
  Avatar,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemText,
  TextField,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Close as CloseIcon, Place as PlaceIcon, Delete as DeleteIcon, AutoAwesome as AutoAwesomeIcon, Edit as EditIcon } from '@mui/icons-material';
import { getBudgets } from '../services/budgetService';
import { assignBudgetCategory, categorizeSimilarTransactions, setBudget, markReimbursable } from '../services/transactionService';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { ThumbUp, ThumbDown } from '@mui/icons-material';

// 4xx `error` is the message the user can act on. 5xx bodies can be raw
// exception text, so those keep the fallback (#1741).
const clientErrorMessage = (error, fallback) => {
  const status = error?.response?.status;
  if (typeof status !== 'number' || status < 400 || status >= 500) return fallback;
  const bodyError = error.response?.data?.error;
  if (typeof bodyError === 'string' && bodyError.trim()) return bodyError.trim();
  if (bodyError && typeof bodyError.message === 'string' && bodyError.message.trim()) {
    return bodyError.message.trim();
  }
  return fallback;
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px',
    padding: theme.spacing(4),
    width: '100%',
    maxWidth: 500
  }
}));

const AmountDisplay = styled(Typography)(({ theme, negative }) => ({
  fontSize: '2rem',
  fontWeight: 700,
  color: negative === 'true' ? theme.palette.error.main : theme.palette.success.main,
  textAlign: 'center',
  marginBottom: theme.spacing(1)
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderBottom: 'none'
  }
}));

const DetailLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: '0.875rem'
}));

const DetailValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 400,
  textAlign: 'right'
}));

const MapContainer = styled(Paper)(({ theme }) => ({
  height: 200,
  width: '100%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: '12px',
  overflow: 'hidden',
  position: 'relative'
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1)
}));

const LoadingOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 99999 // Very high z-index
}));

// Budget period helpers — mirror backend utils/budgetPeriod.js semantics.
// Budget boundaries are stored as UTC instants (start at midnight, end at 23:59:59 UTC),
// so display/comparison reads them back in UTC to avoid timezone drift.
const toDateSafe = (value) => {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const isDateInBudget = (date, budget) => {
  const d = toDateSafe(date);
  const start = toDateSafe(budget?.startDate);
  if (!d || !start) return false;
  const end = toDateSafe(budget?.endDate);
  if (end) return d >= start && d <= end;
  return d >= start;
};

const formatBudgetPeriodRange = (budget) => {
  const start = toDateSafe(budget?.startDate);
  if (!start) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' };
  const startLabel = start.toLocaleDateString('en-US', opts);
  const end = toDateSafe(budget?.endDate);
  return end ? `${startLabel} – ${end.toLocaleDateString('en-US', opts)}` : `From ${startLabel}`;
};

const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

const REIMBURSEMENT_STATUS_LABELS = {
  candidate: 'pending',
  partial: 'partially repaid',
  settled: 'repaid'
};

const TransactionDetailModal = ({ open, onClose, transaction, onTransactionUpdate }) => {
  const { hasFeature, user } = useAuth();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Budget override state
  const [budgetMenuAnchor, setBudgetMenuAnchor] = useState(null);
  const [pendingBudgetMove, setPendingBudgetMove] = useState(null);

  // Reimbursable state
  const [reimburseDialogOpen, setReimburseDialogOpen] = useState(false);
  const [reimburseAmount, setReimburseAmount] = useState('');
  const [reimburseError, setReimburseError] = useState(null);

  // NEW: Reimbursement linking state
  const [showReimbursementDialog, setShowReimbursementDialog] = useState(false);
  const [reimbursementTransactions, setReimbursementTransactions] = useState([]);
  const [loadingReimbursements, setLoadingReimbursements] = useState(false);

  const LOW_CONFIDENCE_EXPLAINED_KEY = 'budgit:lowConfidenceIndicatorExplained:v1';
  const confidenceThreshold = (user?.preferences?.ai?.confidenceThreshold ?? 70) / 100;
  const isLowConfidence = (() => {
    const assignedBy = transaction?.budgetCategory?.assignedBy;
    const confidence = transaction?.budgetCategory?.confidence;
    const isAIAssigned = assignedBy === 'ai' || assignedBy === 'ai_service';
    const isReviewed = Boolean(transaction?.aiFeedback?.providedAt) || assignedBy === 'user';
    return isAIAssigned && !isReviewed && typeof confidence === 'number' && confidence < confidenceThreshold;
  })();

  useEffect(() => {
    try {
      if (!open || !transaction?._id) return;
      if (!isLowConfidence) return;
      const alreadyExplained = localStorage.getItem(LOW_CONFIDENCE_EXPLAINED_KEY);
      if (alreadyExplained) return;
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The sparkle icon means the AI wasn’t confident about this category. Please confirm it (thumbs up) or change it before approving your budget.'
      });
      localStorage.setItem(LOW_CONFIDENCE_EXPLAINED_KEY, 'true');
    } catch (e) {
      // Non-fatal
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction?._id, isLowConfidence]);

  useEffect(() => {
    if (open) {
      loadBudgets();
    }
  }, [open]);

  useEffect(() => {
    if (transaction?.budgetCategory?.id) {
      setSelectedCategory(`${transaction.budget}_${transaction.budgetCategory.id}`);
    } else {
      setSelectedCategory('');
    }
  }, [transaction]);

  useEffect(() => {
    if (!open) {
      setSnackbar({ open: false, message: '', severity: 'success' });
      setBudgetMenuAnchor(null);
      setPendingBudgetMove(null);
      setReimburseDialogOpen(false);
      setReimburseError(null);
    }
  }, [open]);

  const txId = transaction?._id;
  useEffect(() => {
    if (transaction) {
      setSnackbar({ open: false, message: '', severity: 'success' });
    }
  }, [txId, transaction]);

  const loadBudgets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBudgets();
      setBudgets(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading budgets:', error);
      setError('Failed to load budgets');
      setBudgets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = () => {
    setCategoryDialogOpen(true);
  };

  const handleCategorySelect = async (budgetId, categoryId, categoryName) => {
    try {
      setLoading(true);
      
      // If budgetId and categoryId are null, we're uncategorizing via backend
      if (budgetId === null && categoryId === null) {
        // Call backend to persist the uncategorize action
        // Backend clears budgetCategory and allocations but keeps transaction.budget unchanged
        const response = await assignBudgetCategory(transaction._id, null, null);
        setSelectedCategory('');
        if (onTransactionUpdate) {
          await onTransactionUpdate(response.data);
        }
        setCategoryDialogOpen(false);
        return;
      }

      // Otherwise, proceed with normal category assignment
      const response = await assignBudgetCategory(transaction._id, budgetId, categoryId);
      setSelectedCategory(`${budgetId}_${categoryId}`);
      if (onTransactionUpdate) {
        await onTransactionUpdate(response.data);
      }
      setCategoryDialogOpen(false);
    } catch (error) {
      console.error('Error assigning category:', error);
      setError('Failed to assign category');
    } finally {
      setLoading(false);
    }
  };

  const currentBudgetId = transaction?.budget
    ? (typeof transaction.budget === 'object' ? transaction.budget._id : transaction.budget)
    : null;
  const connectedBudget = currentBudgetId
    ? budgets.find(b => b._id === currentBudgetId) || null
    : null;

  const txDate = toDateSafe(transaction?.date);
  const budgetOptions = txDate
    ? budgets
        .filter(b => {
          const start = toDateSafe(b.startDate);
          return start && Math.abs(start - txDate) <= ONE_YEAR_MS;
        })
        .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))
    : [];

  const handleApplyBudget = async (budgetId) => {
    try {
      setLoading(true);
      const response = await setBudget(transaction._id, budgetId);
      const updated = response.data;
      if (updated?.budgetChangeResult?.categoriesCleared) {
        setSelectedCategory('');
      }
      if (onTransactionUpdate) {
        await onTransactionUpdate(updated);
      }
      if (updated?.budgetChangeResult?.categoriesCleared) {
        setSnackbar({
          open: true,
          message: 'Moved. Pick a category for it in that month\'s budget.',
          severity: 'info'
        });
      }
    } catch (error) {
      console.error('Error moving transaction to another budget:', error);
      const data = error.response?.data;
      const serverMessage = data?.error?.message ||
        (typeof data?.error === 'string' ? data.error : null) ||
        data?.message;
      setSnackbar({
        open: true,
        message: serverMessage || 'Failed to move transaction',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBudgetSelect = (budget) => {
    setBudgetMenuAnchor(null);
    if (budget._id === currentBudgetId) {
      return;
    }

    // Selecting the budget that covers the actual date reverts the override
    const targetBudgetId = isDateInBudget(transaction.date, budget) ? null : budget._id;

    // Confirm when the move crosses into/out of a budget period containing today
    const now = new Date();
    const crossesCurrentPeriod =
      (connectedBudget && isDateInBudget(now, connectedBudget)) ||
      isDateInBudget(now, budget);

    if (crossesCurrentPeriod) {
      setPendingBudgetMove({ budgetId: targetBudgetId });
      return;
    }

    handleApplyBudget(targetBudgetId);
  };

  const handleConfirmBudgetMove = () => {
    const { budgetId } = pendingBudgetMove;
    setPendingBudgetMove(null);
    handleApplyBudget(budgetId);
  };

  const handleGetAISuggestion = async () => {
    if (!hasFeature('ai_categorization')) {
      setSnackbar({
        open: true,
        message: 'Upgrade to Premium to use AI-powered categorization',
        severity: 'info'
      });
      return;
    }

    try {
      setLoadingSuggestion(true);
      const response = await api.get(`/ai/suggest-category/${transaction._id}`);
      if (response.data.success) {
        setAiSuggestion(response.data.data);
      }
    } catch (error) {
      console.error('Error getting AI suggestion:', error);
      setSnackbar({
        open: true,
        message: 'Failed to get AI suggestion',
        severity: 'error'
      });
    } finally {
      setLoadingSuggestion(false);
    }
  };

  const handleApplySuggestion = async () => {
    if (!aiSuggestion) return;

    try {
      setLoading(true);
      // Find the category in budgets
      const activeBudget = budgets.find(b => b.isActive);
      if (!activeBudget) {
        setSnackbar({
          open: true,
          message: 'No active budget found',
          severity: 'error'
        });
        return;
      }

      // First try to use the stored categoryId (most reliable), fallback to name lookup
      let category = aiSuggestion.categoryId 
        ? activeBudget.categories.find(c => c._id === aiSuggestion.categoryId)
        : null;
      
      // Fallback to name lookup if categoryId not found (category may have been recreated)
      if (!category && aiSuggestion.categoryName) {
        category = activeBudget.categories.find(c => c.name === aiSuggestion.categoryName);
      }

      if (!category) {
        setSnackbar({
          open: true,
          message: `Category "${aiSuggestion.categoryName}" not found in budget`,
          severity: 'error'
        });
        return;
      }

      await handleCategorySelect(activeBudget._id, category._id, category.name);
      setAiSuggestion(null);
      setSnackbar({
        open: true,
        message: 'AI suggestion applied successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error applying suggestion:', error);
      setSnackbar({
        open: true,
        message: 'Failed to apply suggestion',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!transaction?._id) return;

    // Only allow deletion of manual transactions
    if (transaction.source !== 'manual') {
      setSnackbar({
        open: true,
        message: 'Only manual transactions can be deleted',
        severity: 'error'
      });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      await api.delete(`/transactions/${transaction._id}`);
      setSnackbar({
        open: true,
        message: 'Transaction deleted successfully',
        severity: 'success'
      });
      
      // Notify parent to refresh
      if (onTransactionUpdate) {
        onTransactionUpdate();
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error('Error deleting transaction:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete transaction';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleFeedback = async (approved) => {
    if (!hasFeature('ai_categorization')) {
      setSnackbar({
        open: true,
        message: 'Upgrade to Premium to provide feedback',
        severity: 'info'
      });
      return;
    }

    const hasAICategory = transaction.budgetCategory?.assignedBy === 'ai' || 
                         transaction.budgetCategory?.assignedBy === 'ai_service';
    
    if (!hasAICategory) {
      setSnackbar({
        open: true,
        message: 'This transaction does not have an AI-assigned category',
        severity: 'warning'
      });
      return;
    }

    try {
      setSubmittingFeedback(true);
      await api.post(`/ai/feedback/${transaction._id}`, { approved });
      setSnackbar({
        open: true,
        message: approved ? 'Thank you for your feedback!' : 'Feedback recorded',
        severity: 'success'
      });
      // Refresh transaction if callback provided
      if (onTransactionUpdate && transaction) {
        const updated = await api.get(`/transactions/${transaction._id}`);
        // Ensure we pass the full transaction object to the callback
        const fullTransaction = updated.data && typeof updated.data === 'object' && !Array.isArray(updated.data)
          ? updated.data
          : updated;
        await onTransactionUpdate(fullTransaction);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSnackbar({
        open: true,
        message: 'Failed to submit feedback',
        severity: 'error'
      });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Handle toggle transfer status
  const handleToggleTransfer = async () => {
    if (!transaction?._id) return;
    
    try {
      setLoading(true);
      const isCurrentlyTransfer = transaction.internalTransfer?.isTransfer;
      const endpoint = isCurrentlyTransfer 
        ? `/transactions/${transaction._id}/unmark-transfer`
        : `/transactions/${transaction._id}/mark-transfer`;
      
      const response = await api.post(endpoint);
      
      if (isCurrentlyTransfer) {
        // Unmarked as transfer - don't change selectedCategory
      } else {
        // Marked as transfer - clear category selection
        setSelectedCategory('');
      }
      
      if (onTransactionUpdate) {
        await onTransactionUpdate(response.data.data);
      }
      
      setSnackbar({
        open: true,
        message: isCurrentlyTransfer ? 'Removed transfer marking' : 'Marked as internal transfer',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling transfer:', error);
      setSnackbar({
        open: true,
        message: clientErrorMessage(error, 'Failed to update transfer status'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle toggle income status
  const handleToggleIncome = async () => {
    if (!transaction?._id) return;
    
    try {
      setLoading(true);
      const isCurrentlyIncome = transaction.incomeOverride?.isIncome;
      const endpoint = isCurrentlyIncome 
        ? `/transactions/${transaction._id}/unmark-income`
        : `/transactions/${transaction._id}/mark-income`;
      
      const response = await api.post(endpoint);
      
      if (!isCurrentlyIncome) {
        // Marked as income - clear category selection
        setSelectedCategory('');
      }
      
      if (onTransactionUpdate) {
        await onTransactionUpdate(response.data.data);
      }
      
      setSnackbar({
        open: true,
        message: isCurrentlyIncome ? 'Removed income marking' : 'Marked as income',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error toggling income:', error);
      setSnackbar({
        open: true,
        message: clientErrorMessage(error, 'Failed to update income status'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Reimbursable marking — carried on the transaction's single allocation
  const reimburseAllocation = transaction?.allocations?.[0] || null;
  const reimbursableCents = reimburseAllocation?.reimbursableCents ?? 0;
  const isReimbursable = reimbursableCents > 0;
  const reimbursementStatusLabel =
    REIMBURSEMENT_STATUS_LABELS[reimburseAllocation?.reimbursementStatus] || 'pending';
  const txAmountCents = transaction?.amountCents ?? Math.round(Math.abs(transaction?.amount ?? 0) * 100);
  const maxReimbursableCents = reimburseAllocation?.amountCents ?? txAmountCents;
  const canMarkReimbursable =
    (transaction?.amount ?? 0) > 0 &&
    !transaction?.pending &&
    (transaction?.allocations?.length ?? 0) <= 1;

  const parseReimburseCents = (value) => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return null;
    return Math.round(parsed * 100);
  };

  const handleOpenReimburseDialog = () => {
    setReimburseAmount((maxReimbursableCents / 100).toFixed(2));
    setReimburseError(null);
    setReimburseDialogOpen(true);
  };

  const applyReimbursable = async (cents, successMessage) => {
    try {
      setLoading(true);
      const response = await markReimbursable(transaction._id, cents);
      if (onTransactionUpdate) {
        await onTransactionUpdate(response.data.transaction);
      }
      setReimburseDialogOpen(false);
      setSnackbar({ open: true, message: successMessage, severity: 'success' });
    } catch (error) {
      console.error('Error updating reimbursable status:', error);
      const serverError = error.response?.data?.error;
      setSnackbar({
        open: true,
        message: (typeof serverError === 'string' ? serverError : serverError?.message) ||
          'Failed to update reimbursable status',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMarkReimbursable = () => {
    const cents = parseReimburseCents(reimburseAmount);
    if (cents === null || cents <= 0 || cents > maxReimbursableCents) {
      setReimburseError(
        `Enter an amount between $0.01 and $${(maxReimbursableCents / 100).toFixed(2)}`
      );
      return;
    }
    applyReimbursable(cents, 'Marked as reimbursable');
  };

  const handleUnmarkReimbursable = () => {
    applyReimbursable(0, 'Reimbursable marking removed');
  };

  // NEW: Load reimbursement transactions
  const loadReimbursementTransactions = async () => {
    try {
      setLoadingReimbursements(true);
      const { getReimbursementTransactions } = await import('../services/api');
      const response = await getReimbursementTransactions(90);
      setReimbursementTransactions(response.transactions || []);
    } catch (error) {
      console.error('Error loading reimbursement transactions:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load reimbursement transactions',
        severity: 'error'
      });
    } finally {
      setLoadingReimbursements(false);
    }
  };
  
  // NEW: Handle reimbursement linking
  const handleLinkReimbursement = async (reimbursementTxn) => {
    if (!transaction?._id) return;

    try {
      const { linkReimbursement } = await import('../services/api');
      const reimbursementAmount = Math.abs(reimbursementTxn.amountCents);

      setLoading(true);
      await linkReimbursement(transaction._id, reimbursementTxn._id, reimbursementAmount);
      
      setSnackbar({
        open: true,
        message: 'Reimbursement linked successfully',
        severity: 'success'
      });
      setShowReimbursementDialog(false);
      
      // Refresh transaction
      if (onTransactionUpdate) {
        const response = await api.get(`/transactions`, { 
          params: { limit: 1, search: transaction._id, includeDetails: true } 
        });
        const transactions = response.data?.transactions || [];
        const updated = transactions.find(t => t._id === transaction._id);
        if (updated) {
          await onTransactionUpdate(updated);
        }
      }
    } catch (error) {
      console.error('Error linking reimbursement:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to link reimbursement',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCategorizeSimilar = async () => {
    if (!selectedCategory) {
      setSnackbar({
        open: true,
        message: 'Please categorize this transaction first',
        severity: 'warning'
      });
      return;
    }

    try {
      setLoading(true);
      const [budgetId, categoryId] = selectedCategory.split('_');
      
      const result = await categorizeSimilarTransactions(
        transaction._id,
        budgetId,
        categoryId
      );
      
      const budget = budgets.find(b => b._id === budgetId);
      const category = budget?.categories.find(c => c._id === categoryId);
      
      const categoryInfo = {
        budget: budgetId,
        budgetCategory: {
          id: categoryId,
          name: category?.name,
          icon: category?.icon,
          assignedAt: new Date(),
          assignedBy: 'user'
        }
      };

      const updatedTransaction = {
        ...transaction,
        ...categoryInfo,
        bulkUpdate: true,
        similarCriteria: {
          merchantName: transaction.merchantName,
          description: transaction.description,
          merchantEntityId: transaction.merchantEntityId,
          categoryInfo
        },
        updatedCount: result.results.success
      };
      
      if (onTransactionUpdate) {
        await onTransactionUpdate(updatedTransaction);
      }
      
      setSnackbar({
        open: true,
        message: `Successfully categorized ${result.results.success} similar transactions`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error categorizing similar transactions:', err);
      setSnackbar({
        open: true,
        message: 'Failed to categorize similar transactions',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const formatAmount = (amount, isoCurrencyCode = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: isoCurrencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Math.abs(amount));
  };

  const formatDate = (date, includeTime = false) => {
    if (!date) return null;
    
    try {
      if (includeTime) {
        // For times: use full datetime parsing to convert UTC to local timezone
        const localDate = new Date(date);
        return localDate.toLocaleString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          timeZoneName: 'short'
        });
      }
      
      // For date-only display: extract the date part (YYYY-MM-DD) to preserve calendar date
      // This prevents timezone conversion issues (e.g., UTC midnight showing as previous day in US timezones)
      const dateStr = typeof date === 'string'
        ? date.split('T')[0]  // Extract YYYY-MM-DD part from ISO string
        : new Date(date).toISOString().split('T')[0];
      
      // Parse as local date (not UTC) to preserve the calendar date value
      const [year, month, day] = dateStr.split('-').map(Number);
      const txDate = new Date(year, month - 1, day);
      
      return txDate.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return date;
    }
  };

  const renderMap = () => {
    if (!transaction?.location?.lat || !transaction?.location?.lon || !process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
      return null;
    }

    const mapUrl = `https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${transaction.location.lat},${transaction.location.lon}&zoom=15`;

    return (
      <Box>
        <SectionTitle variant="subtitle2">Location</SectionTitle>
        <MapContainer>
          <iframe
            title="Transaction Location"
            width="100%"
            height="100%"
            frameBorder="0"
            src={mapUrl}
            allowFullScreen
          />
        </MapContainer>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaceIcon fontSize="small" />
          {[
            transaction.location.address,
            transaction.location.city,
            transaction.location.region,
            transaction.location.postalCode,
            transaction.location.country
          ].filter(Boolean).join(', ')}
        </Typography>
      </Box>
    );
  };

  const renderDetailSection = (title, details) => {
    const filteredDetails = Object.entries(details).filter(([_, value]) => value != null);
    if (filteredDetails.length === 0) return null;

    return (
      <Box>
        <SectionTitle variant="subtitle2">{title}</SectionTitle>
        {filteredDetails.map(([key, value]) => (
          <DetailRow key={key}>
            <DetailLabel>{key.replace(/([A-Z])/g, ' $1').trim()}</DetailLabel>
            <DetailValue>{
              typeof value === 'boolean' 
                ? value.toString() 
                : value instanceof Date 
                  ? formatDate(value)
                  : value
            }</DetailValue>
          </DetailRow>
        ))}
      </Box>
    );
  };

  if (!transaction) return null;

  return (
    <>
      {loading && (
        <LoadingOverlay>
          <CircularProgress />
        </LoadingOverlay>
      )}
      
      <StyledDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ p: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Header Section */}
          <Box sx={{ 
            mb: 3, 
            pb: 3, 
            borderBottom: 1, 
            borderColor: 'divider',
            textAlign: 'center' 
          }}>
            <AmountDisplay negative={(transaction.amount > 0).toString()}>
              {transaction.amount > 0 ? '-' : ''}{formatAmount(transaction.amount, transaction.isoCurrencyCode)}
            </AmountDisplay>
            <Typography variant="h6" gutterBottom>
              {transaction.merchantName || transaction.description}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {formatDate(transaction.authorizedDate || transaction.date, false)}
              </Typography>
              {transaction.pending && (
                <Chip
                  label="Pending"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
            {!transaction.pending && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Budget: {connectedBudget
                    ? `${connectedBudget.name} (${formatBudgetPeriodRange(connectedBudget)})`
                    : 'None'}
                </Typography>
                {transaction.budgetDate && (
                  <Chip
                    label="Moved"
                    size="small"
                    color="primary"
                  />
                )}
                <IconButton
                  size="small"
                  aria-label="Change budget"
                  onClick={(event) => setBudgetMenuAnchor(event.currentTarget)}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
            {(transaction?.account?.name || transaction?.account?.mask) && (
              <Typography variant="body2" color="text.secondary">
                {transaction.account.displayName || transaction.account.name}{transaction.account.mask ? ` •••• ${transaction.account.mask}` : ''}
              </Typography>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 2 }}>
              {error ? (
                <Typography color="error" variant="body2">{error}</Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={selectedCategory ? budgets
                        .find(b => selectedCategory.startsWith(b._id))
                        ?.categories.find(c => selectedCategory.endsWith(c._id))?.name || 'Uncategorized'
                        : 'Uncategorized'
                      }
                      onClick={handleCategoryClick}
                      sx={{
                        borderRadius: '16px',
                        bgcolor: selectedCategory ? 'primary.main' : 'grey.200',
                        color: selectedCategory ? 'white' : 'text.secondary',
                        '&:hover': {
                          bgcolor: selectedCategory ? 'primary.dark' : 'grey.300',
                        },
                        fontWeight: 500,
                        px: 2
                      }}
                    />
                    {isLowConfidence && (
                      <Tooltip title="Low confidence AI categorization — confirm (thumbs up) or change the category before approving your budget">
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AutoAwesomeIcon sx={{ fontSize: 18, color: (theme) => theme.palette.warning.main }} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                  
                  {/* Get AI Suggestion Button (for uncategorized transactions) */}
                  {!selectedCategory && hasFeature('ai_categorization') && (
                    <Button
                      variant="outlined"
                      onClick={handleGetAISuggestion}
                      disabled={loadingSuggestion}
                      sx={{ borderRadius: '50px', textTransform: 'none' }}
                    >
                      {loadingSuggestion ? <CircularProgress size={20} /> : 'Get AI Suggestion'}
                    </Button>
                  )}

                  {/* AI Suggestion Display */}
                  {aiSuggestion && !selectedCategory && (
                    <Box sx={{ 
                      p: 2, 
                      bgcolor: 'info.light', 
                      borderRadius: '12px', 
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        AI Suggestion: {aiSuggestion.categoryName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Confidence: {(aiSuggestion.confidence * 100).toFixed(0)}%
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleApplySuggestion}
                        disabled={loading}
                        size="small"
                        sx={{ mt: 1, borderRadius: '50px', textTransform: 'none' }}
                      >
                        Apply Suggestion
                      </Button>
                    </Box>
                  )}

                  {/* Feedback Buttons (for AI-assigned categories) */}
                  {selectedCategory && (transaction.budgetCategory?.assignedBy === 'ai' || transaction.budgetCategory?.assignedBy === 'ai_service') && hasFeature('ai_categorization') && (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        Was this helpful?
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(true)}
                        disabled={submittingFeedback}
                        color="success"
                        sx={{ 
                          bgcolor: 'success.light',
                          '&:hover': { bgcolor: 'success.main' }
                        }}
                      >
                        <ThumbUp fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleFeedback(false)}
                        disabled={submittingFeedback}
                        color="error"
                        sx={{ 
                          bgcolor: 'error.light',
                          '&:hover': { bgcolor: 'error.main' }
                        }}
                      >
                        <ThumbDown fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </>
              )}
              
              {/* Note: Full multi-category line-item editor UI can be added here */}
              {/* For now, users can use the applySplits API via manual category assignment */}
              {/* A complete line-item editor matching the entry screen is a future enhancement */}
            </Box>
            
            {/* Transfer Toggle */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={handleToggleTransfer}
            >
              <Chip
                icon={transaction?.internalTransfer?.isTransfer ? 
                  <span style={{ fontSize: '18px' }}>☑️</span> : 
                  <span style={{ fontSize: '18px' }}>⬜</span>
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Mark as Internal Transfer
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Transfers between your accounts are excluded from budget totals
                    </Typography>
                  </Box>
                }
                variant={transaction?.internalTransfer?.isTransfer ? "filled" : "outlined"}
                color={transaction?.internalTransfer?.isTransfer ? "primary" : "default"}
                sx={{ 
                  height: 'auto',
                  py: 1,
                  px: 1.5,
                  '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' }
                }}
              />
            </Box>
            
            {/* Income Toggle */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mt: 2,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={handleToggleIncome}
            >
              <Chip
                icon={transaction?.incomeOverride?.isIncome ? 
                  <span style={{ fontSize: '18px' }}>☑️</span> : 
                  <span style={{ fontSize: '18px' }}>⬜</span>
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Mark as Income
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Include this transaction in your income calculation
                    </Typography>
                  </Box>
                }
                variant={transaction?.incomeOverride?.isIncome ? "filled" : "outlined"}
                color={transaction?.incomeOverride?.isIncome ? "success" : "default"}
                sx={{
                  height: 'auto',
                  py: 1,
                  px: 1.5,
                  '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' }
                }}
              />
            </Box>

            {/* Reimbursable Toggle (expenses only, single or no allocation) */}
            {canMarkReimbursable && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  mt: 2,
                  p: 2,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'grey.100' }
                }}
                onClick={isReimbursable ? handleUnmarkReimbursable : handleOpenReimburseDialog}
              >
                <Chip
                  onClick={(e) => {
                    e.stopPropagation();
                    (isReimbursable ? handleUnmarkReimbursable : handleOpenReimburseDialog)();
                  }}
                  icon={isReimbursable ?
                    <span style={{ fontSize: '18px' }}>☑️</span> :
                    <span style={{ fontSize: '18px' }}>⬜</span>
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {isReimbursable
                          ? `${formatAmount(reimbursableCents / 100, transaction.isoCurrencyCode)} owed back · ${reimbursementStatusLabel}`
                          : 'Mark as reimbursable'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isReimbursable
                          ? 'Tap to unmark this expense as reimbursable'
                          : 'Track money someone owes you back for this expense'}
                      </Typography>
                    </Box>
                  }
                  variant={isReimbursable ? "filled" : "outlined"}
                  color={isReimbursable ? "info" : "default"}
                  sx={{
                    height: 'auto',
                    py: 1,
                    px: 1.5,
                    '& .MuiChip-label': { whiteSpace: 'normal', display: 'block' }
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Location Map */}
          {renderMap()}

          {/* Transaction Details */}
          {transaction.counterparties?.length > 0 && (
            <Box>
              <SectionTitle variant="subtitle2">Counterparties</SectionTitle>
              {transaction.counterparties.map((cp, index) => (
                <Box key={index} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    {cp.logoUrl && (
                      <Avatar
                        src={cp.logoUrl}
                        alt={cp.name}
                        sx={{ width: 24, height: 24 }}
                      />
                    )}
                    <Typography variant="subtitle2">{cp.name}</Typography>
                    <Chip 
                      label={cp.type} 
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        bgcolor: 'grey.100'
                      }}
                    />
                  </Box>
                  {cp.website && (
                    <Typography 
                      variant="body2" 
                      component="a"
                      href={cp.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {cp.website}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {renderDetailSection('Payment Details', {
            'Payment Channel': transaction.paymentChannel,
            'Transaction Type': transaction.type || (transaction.internalTransfer?.isTransfer ? 'transfer' : (transaction.amount < 0 ? 'income' : 'expense')),
            'Income Subtype': transaction.type === 'income' ? transaction.subtype : undefined,
            'Plaid Transaction Type': transaction.plaidTransactionType,
            'By Order Of': transaction.paymentMeta?.byOrderOf,
            'Payee': transaction.paymentMeta?.payee,
            'Payer': transaction.paymentMeta?.payer,
            'Payment Method': transaction.paymentMeta?.paymentMethod,
            'Payment Processor': transaction.paymentMeta?.paymentProcessor,
            'PPD ID': transaction.paymentMeta?.ppdId,
            'Reason': transaction.paymentMeta?.reason,
            'Reference Number': transaction.paymentMeta?.referenceNumber
          })}

          {renderDetailSection('Timing', {
            'Transaction Date': formatDate(transaction.authorizedDate || transaction.date, false),
            'Authorized Date': formatDate(transaction.authorizedDate, false),
            'Authorized Time': formatDate(transaction.authorizedDatetime, true),
            'Posted Time': formatDate(transaction.datetime, true)
          })}

          {/* AI Categorization Section - Only show if premium */}
          {transaction.personalFinanceCategory && hasFeature('ai_categorization') && (
            <Box>
              <SectionTitle variant="subtitle2">AI Categorization</SectionTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                {transaction.personalFinanceCategory.iconUrl && (
                  <Avatar
                    src={transaction.personalFinanceCategory.iconUrl}
                    alt={transaction.personalFinanceCategory.primary}
                    sx={{ width: 24, height: 24 }}
                  />
                )}
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                    {transaction.personalFinanceCategory.primary}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {transaction.personalFinanceCategory.detailed}
                  </Typography>
                </Box>
                <Chip
                  label={`${(transaction.personalFinanceCategory.confidence * 100).toFixed(0)}%`}
                  size="small"
                  color={
                    transaction.personalFinanceCategory.confidence > 0.8 ? 'success' :
                    transaction.personalFinanceCategory.confidence > 0.6 ? 'info' :
                    transaction.personalFinanceCategory.confidence > 0.4 ? 'warning' : 'error'
                  }
                />
              </Box>
            </Box>
          )}

          {/* Original Description */}
          {transaction.originalDescription && (
            <Box>
              <SectionTitle variant="subtitle2">Original Description</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {transaction.originalDescription}
              </Typography>
            </Box>
          )}

          {/* Budget Picker Menu */}
          <Menu
            anchorEl={budgetMenuAnchor}
            open={Boolean(budgetMenuAnchor)}
            onClose={() => setBudgetMenuAnchor(null)}
          >
            {budgetOptions.length === 0 && (
              <MenuItem disabled>No budgets available</MenuItem>
            )}
            {budgetOptions.map((budget) => (
              <MenuItem
                key={budget._id}
                selected={budget._id === currentBudgetId}
                onClick={() => handleBudgetSelect(budget)}
              >
                <ListItemText
                  primary={`${budget.name}${isDateInBudget(transaction.date, budget) ? ' (actual date)' : ''}`}
                  secondary={formatBudgetPeriodRange(budget)}
                />
              </MenuItem>
            ))}
          </Menu>

          {/* Budget Move Confirmation Dialog */}
          <Dialog
            open={Boolean(pendingBudgetMove)}
            onClose={() => setPendingBudgetMove(null)}
            PaperProps={{
              sx: {
                borderRadius: '20px',
                p: 2
              }
            }}
          >
            <DialogTitle>Move this transaction?</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                Moving spending in or out of the current month removes you from this month's leaderboard contest. Move anyway?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPendingBudgetMove(null)}>
                Cancel
              </Button>
              <Button color="warning" onClick={handleConfirmBudgetMove}>
                Move anyway
              </Button>
            </DialogActions>
          </Dialog>

          {/* Mark as Reimbursable Dialog */}
          <Dialog
            open={reimburseDialogOpen}
            onClose={() => setReimburseDialogOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: '20px',
                p: 2,
                width: '100%',
                maxWidth: 360
              }
            }}
          >
            <DialogTitle>Mark as reimbursable</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How much of this expense do you expect to be paid back?
              </Typography>
              <TextField
                fullWidth
                label="Amount"
                value={reimburseAmount}
                onChange={(event) => {
                  setReimburseAmount(event.target.value);
                  setReimburseError(null);
                }}
                error={Boolean(reimburseError)}
                helperText={reimburseError || ''}
                inputProps={{ inputMode: 'decimal' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setReimburseAmount((maxReimbursableCents / 100).toFixed(2));
                    setReimburseError(null);
                  }}
                >
                  Full amount
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setReimburseAmount((Math.round(maxReimbursableCents / 2) / 100).toFixed(2));
                    setReimburseError(null);
                  }}
                >
                  Half
                </Button>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setReimburseDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleConfirmMarkReimbursable} disabled={loading}>
                Mark as reimbursable
              </Button>
            </DialogActions>
          </Dialog>

          {/* Category Selection Dialog */}
          <Dialog
            open={categoryDialogOpen}
            onClose={() => setCategoryDialogOpen(false)}
            PaperProps={{
              sx: {
                borderRadius: '20px',
                width: '100%',
                maxWidth: 400,
                p: 2
              }
            }}
          >
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Select Category</Typography>
                <IconButton onClick={() => setCategoryDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Chip
                  label="Uncategorized"
                  onClick={() => handleCategorySelect(null, null)}
                  sx={{
                    borderRadius: '16px',
                    bgcolor: !selectedCategory ? 'primary.main' : 'grey.200',
                    color: !selectedCategory ? 'white' : 'text.secondary',
                    '&:hover': {
                      bgcolor: !selectedCategory ? 'primary.dark' : 'grey.300',
                    }
                  }}
                />
                {budgets.map(budget => (
                  <Box key={budget._id}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {budget.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {budget.categories.map(category => (
                        <Chip
                          key={category._id}
                          label={category.name}
                          onClick={() => handleCategorySelect(budget._id, category._id, category.name)}
                          sx={{
                            borderRadius: '50px',
                            bgcolor: selectedCategory === `${budget._id}_${category._id}` ? 'primary.main' : 'grey.200',
                            color: selectedCategory === `${budget._id}_${category._id}` ? 'white' : 'text.secondary',
                            '&:hover': {
                              bgcolor: selectedCategory === `${budget._id}_${category._id}` ? 'primary.dark' : 'grey.300',
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            </DialogContent>
          </Dialog>

          {selectedCategory && (
            <Box mt={2} display="flex" justifyContent="center">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleCategorizeSimilar}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Categorizing...' : 'Auto-Categorize Similar'}
              </Button>
            </Box>
          )}

          {/* Link Reimbursement Button (only for expenses) */}
          {transaction?.amount < 0 && (
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  loadReimbursementTransactions();
                  setShowReimbursementDialog(true);
                }}
              >
                Link Reimbursement
              </Button>
            </Box>
          )}

          {/* Delete Button (only for manual transactions) */}
          {transaction?.source === 'manual' && (
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
                disabled={deleting}
                startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
              >
                {deleting ? 'Deleting...' : 'Delete Transaction'}
              </Button>
            </Box>
          )}
        </DialogContent>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </StyledDialog>
      
      {/* Reimbursement Lookup Dialog */}
      <Dialog
        open={showReimbursementDialog}
        onClose={() => setShowReimbursementDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '20px', p: 2 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Select Reimbursement</Typography>
            <IconButton onClick={() => setShowReimbursementDialog(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loadingReimbursements ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : reimbursementTransactions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
              No reimbursement transactions found
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {reimbursementTransactions.map((reimb) => (
                <Paper
                  key={reimb._id}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.100' },
                    borderRadius: '12px'
                  }}
                  onClick={() => handleLinkReimbursement(reimb)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {reimb.description}
                      </Typography>
                      {reimb.contact?.name && (
                        <Typography variant="body2" color="text.secondary">
                          Contact: {reimb.contact.name}
                        </Typography>
                      )}
                      {reimb.merchantName && (
                        <Typography variant="body2" color="text.secondary">
                          Merchant: {reimb.merchantName}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(reimb.authorizedDate || reimb.date, false)}
                      </Typography>
                    </Box>
                    <Typography variant="h6" color="primary" fontWeight={700}>
                      ${(Math.abs(reimb.amountCents) / 100).toFixed(2)}
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TransactionDetailModal; 